import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useLocation, useNavigationType, useOutlet } from 'react-router-dom';
import styled from 'styled-components';

import { useTopBarHandle } from '../_useTopBarHandle';

import type { Transition, Variants } from 'motion/react';

type Direction = 'push' | 'pop' | 'fade';

const SLIDE_TRANSITION: Transition = { type: 'tween', duration: 0.26, ease: 'easeOut' };
// fade(탭 전환류)는 서로 다른 레이아웃의 두 화면을 크로스페이드하는 거라 원천적으로 깔끔하게 안 나온다 —
// 동시에 겹치면 두 화면이 섞여 보이고, 순서대로 하면 두 번 연속 페이드라 느려 보인다. 네이티브 탭 전환처럼
// 아예 애니메이션 없이 즉시 바꾼다 — duration 0이라 겹치는 프레임 자체가 없어서 부작용이 없다.
const INSTANT_TRANSITION: Transition = { duration: 0 };

// 덮이거나 드러나는 쪽(안 움직이는 화면)이 어두워지는 정도 — iOS 스타일. opacity가 아니라 filter라서
// 배경색과 무관하게 실제로 어두워진다(opacity는 그 화면이 완전 불투명해도 "밑이 비치는 것"처럼 보일 뿐이다).
const DIM_FILTER = 'brightness(0.82)';
const BRIGHT_FILTER = 'brightness(1)';

/**
 * mode='sync'라 나가는 화면과 들어오는 화면이 동시에 존재한다 — 이전엔 mode='wait'로 나가는 화면이 다 빠진
 * 뒤에야 다음 화면이 들어와서, 화면이 비는 순간이 생겨 굼떠 보였다. push/pop은 스택에 카드가 쌓이는 느낌을
 * 내야 해서 "위에 있는 카드" 한 장만 100% 슬라이드하고, 아래 깔리는 화면(안 움직임)은 어두워지기만 한다 —
 * zIndex로 위아래를 정한다. push: 새 화면이 위로 슬라이드 인, 기존 화면은 그 밑에서 어두워짐.
 * pop: 나가는 화면(원래 위 카드)이 위인 채로 슬라이드 아웃, 드러나는 화면은 밝아지며 나타난다.
 * 밝기(filter)가 실제로 바뀌는 애니메이션이라 duration이 무시되지 않는다 — 값이 안 바뀌는 애니메이션은
 * Motion이 "변화 없음"으로 보고 즉시 끝내버려서(→ exit이면 바로 언마운트) 전에는 다른 트릭이 필요했다.
 */
const variants: Variants = {
  enter: (direction: Direction) => {
    if (direction === 'fade') return { opacity: 1, zIndex: 2, transition: INSTANT_TRANSITION };
    if (direction === 'pop') return { x: 0, filter: DIM_FILTER, zIndex: 1, transition: SLIDE_TRANSITION };
    return { x: '100%', filter: BRIGHT_FILTER, zIndex: 2, transition: SLIDE_TRANSITION };
  },
  center: (direction: Direction) => {
    if (direction === 'fade') return { opacity: 1, zIndex: 2, transition: INSTANT_TRANSITION };
    if (direction === 'pop') return { x: 0, filter: BRIGHT_FILTER, zIndex: 1, transition: SLIDE_TRANSITION };
    return { x: 0, filter: BRIGHT_FILTER, zIndex: 2, transition: SLIDE_TRANSITION };
  },
  exit: (direction: Direction) => {
    if (direction === 'fade') return { opacity: 0, zIndex: 1, transition: INSTANT_TRANSITION };
    if (direction === 'pop') return { x: '100%', filter: BRIGHT_FILTER, zIndex: 2, transition: SLIDE_TRANSITION };
    return { x: 0, filter: DIM_FILTER, zIndex: 1, transition: SLIDE_TRANSITION };
  },
};

// reduced-motion에서도 push/pop은 x 분리 없이 같은 자리에서 opacity만 바뀌므로 fade와 동일하게 delay가 필요하다
const REDUCED_EXIT_DURATION = 0.01;
const reducedMotionVariants: Variants = {
  enter: { opacity: 0, zIndex: 2, transition: { duration: REDUCED_EXIT_DURATION } },
  center: { opacity: 1, zIndex: 2, transition: { duration: 0.12, delay: REDUCED_EXIT_DURATION } },
  exit: { opacity: 0, zIndex: 1, transition: { duration: REDUCED_EXIT_DURATION } },
};

interface AnimatedOutletProps {
  hasBottomNav: boolean;
}

/**
 * MainLayout의 <Outlet />을 대신해 화면 전환 애니메이션을 건다.
 * 방향은 navigationType(POP=뒤로가기)과 진입 라우트의 handle.showBack(_useTopBarHandle)만으로 판단한다 —
 * showBack이 있으면 스택으로 파고드는 push, 없으면 탭 전환 등 fade.
 */
export default function AnimatedOutlet({ hasBottomNav }: AnimatedOutletProps) {
  const location = useLocation();
  const navigationType = useNavigationType();
  const { showBack } = useTopBarHandle();
  const prefersReducedMotion = useReducedMotion();
  // <Outlet />은 현재 라우트를 실시간 구독해서, 퇴장 애니메이션용으로 남겨둔 이전 화면도 바뀐 라우트를
  // 다시 그려버린다(새 화면이 두 장 겹쳐 보임). useOutlet()으로 이 시점의 엘리먼트를 값으로 떼어내야 한다.
  const element = useOutlet();

  const direction: Direction = navigationType === 'POP' ? 'pop' : showBack ? 'push' : 'fade';

  return (
    <AnimatePresence mode='sync' initial={false} custom={direction}>
      <S.Page
        key={location.pathname}
        className='custom_scroll'
        $hasBottomNav={hasBottomNav}
        custom={direction}
        variants={prefersReducedMotion ? reducedMotionVariants : variants}
        initial='enter'
        animate='center'
        exit='exit'>
        {element}
      </S.Page>
    </AnimatePresence>
  );
}

const S = {
  Page: styled(motion.div)<{ $hasBottomNav: boolean }>`
    position: absolute;
    inset: 0;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    padding-bottom: ${({ $hasBottomNav }) => ($hasBottomNav ? '0' : 'env(safe-area-inset-bottom)')};
    backface-visibility: hidden;
    /* 각 페이지 컴포넌트가 배경색을 따로 챙기지 않아도 여기서 항상 불투명하게 깔아준다 — 안 그러면 push/pop
       중 밑에 깔린 화면이 비치거나(각 페이지 루트에 배경색이 없는 경우), dim 필터가 무의미해진다. */
    background: var(--color-basic-white, #fefefe);
  `,
};
